// src/app/components/common/Button.jsx
import React from "react";

const Button = ({ 
  children, 
  onClick, 
  type = "button", 
  variant = "primary",
  size = "md",
  loading = false,
  disabled = false,
  fullWidth = false,
  className = "",
  icon = null,
  iconPosition = "left",
  rounded = "lg",
  shadow = true,
  ...props 
}) => {
  const baseClasses = "font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:cursor-not-allowed flex items-center justify-center gap-2";
  
  const variants = {
    primary: "bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 focus:ring-primary-500 active:scale-[0.98]",
    secondary: "bg-gradient-to-r from-gray-800 to-gray-900 text-white border border-gray-700 hover:from-gray-700 hover:to-gray-800 focus:ring-gray-500 active:scale-[0.98]",
    success: "bg-gradient-to-r from-emerald-600 to-emerald-700 text-white hover:from-emerald-700 hover:to-emerald-800 focus:ring-emerald-500 active:scale-[0.98]",
    danger: "bg-gradient-to-r from-rose-600 to-rose-700 text-white hover:from-rose-700 hover:to-rose-800 focus:ring-rose-500 active:scale-[0.98]",
    warning: "bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 focus:ring-amber-500 active:scale-[0.98]",
    outline: "bg-transparent text-gray-800 dark:text-white border-2 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 focus:ring-gray-500 active:scale-[0.98]",
    ghost: "bg-transparent text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 focus:ring-gray-500 active:scale-[0.98]",
    link: "bg-transparent text-primary-600 hover:text-primary-800 underline p-0 focus:ring-primary-500",
  };
  
  const sizes = {
    xs: "px-2 py-1 text-xs min-h-[28px]",
    sm: "px-3 py-1.5 text-sm min-h-[36px]",
    md: "px-4 py-2.5 text-base min-h-[44px]",
    lg: "px-6 py-3 text-lg min-h-[52px]",
    xl: "px-8 py-4 text-xl min-h-[60px]",
  };
  
  const borderRadius = {
    none: "rounded-none",
    sm: "rounded-sm",
    md: "rounded-md",
    lg: "rounded-lg",
    xl: "rounded-xl",
    full: "rounded-full",
  };
  
  const widthClass = fullWidth ? "w-full" : "";
  const disabledClass = disabled || loading ? "opacity-60" : "";
  const shadowClass = shadow && !disabled && !loading ? "shadow-md hover:shadow-lg" : "";
  
  const renderIcon = () => {
    if (!icon) return null;
    
    if (typeof icon === "string") {
      return <span className="material-icons text-sm">{icon}</span>;
    }
    
    return React.cloneElement(icon, {
      className: `w-4 h-4 ${icon.props.className || ""}`
    });
  };
  
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        ${baseClasses}
        ${variants[variant]}
        ${sizes[size]}
        ${borderRadius[rounded]}
        ${widthClass}
        ${disabledClass}
        ${shadowClass}
        ${className}
      `}
      {...props}
    >
      {loading ? (
        <>
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          <span>Processing...</span>
        </>
      ) : (
        <>
          {icon && iconPosition === "left" && renderIcon()}
          {children}
          {icon && iconPosition === "right" && renderIcon()}
        </>
      )}
    </button>
  );
};

export default Button;